/**
 * The tool wheel's non-drawing slots: the drawing bar's mode controls
 * (magnet, lock, hide, remove all), so a slot can flip one instead of arming
 * a tool. A slot holds either an overlay name or one of these ids; the
 * `control:` prefix keeps the two apart.
 *
 * A control runs through the handlers the surface already hands its
 * DrawingBar, so the wheel, the bar and the toolbar's drawing menu always
 * agree. Names and glyphs follow `DrawingToolsMenu`'s rows, and a toggle is
 * named by what a pick will do ("Unlock All", "Show drawings").
 */
import { overlayLabel } from '../utils/overlays'
import { HIDE_MODE_LABELS, type HideMode } from './drawingBarChrome'

/** The mode-control handlers and state every drawing surface gives its DrawingBar. */
export interface WheelControls {
  onSnapToggle?: () => void
  snapActive?: boolean
  onLockAll?: () => void
  lockActive?: boolean
  onSetHidden?: (mode: HideMode, hidden: boolean) => void
  hiddenState?: { drawings: boolean; indicators: boolean }
  onTrashAll?: () => void
}

/** Keys of `ControlIcons` in `drawingBarChrome`. */
export type ControlIconName =
  | 'magnetOn'
  | 'magnetOff'
  | 'lock'
  | 'unlock'
  | 'visible'
  | 'invisible'
  | 'remove'

export interface WheelControl {
  id: string
  /** The control's name in the swap menu. */
  label: string
  /** Whether it is on for this surface (marked on its slice). */
  on: (c: WheelControls) => boolean
  /** What a pick will do now: the slice's tooltip and the hub's title. */
  name: (c: WheelControls) => string
  icon: (on: boolean) => ControlIconName
  /** Whether this surface offers it at all. */
  available: (c: WheelControls) => boolean
  run: (c: WheelControls) => void
}

/** The eye's reading of "hidden", the same as the drawing bar's. */
function isHidden(mode: HideMode, c: WheelControls): boolean {
  const h = c.hiddenState
  if (!h) return false
  if (mode === 'drawings') return h.drawings
  if (mode === 'indicators') return h.indicators
  return h.drawings && h.indicators
}

function hideControl(id: string, mode: HideMode): WheelControl {
  return {
    id,
    label: `Hide ${HIDE_MODE_LABELS[mode]}`,
    on: (c) => isHidden(mode, c),
    name: (c) => `${isHidden(mode, c) ? 'Show' : 'Hide'} ${HIDE_MODE_LABELS[mode]}`,
    icon: (on) => (on ? 'invisible' : 'visible'),
    available: (c) => c.onSetHidden !== undefined,
    run: (c) => c.onSetHidden?.(mode, !isHidden(mode, c)),
  }
}

export const WHEEL_CONTROLS: readonly WheelControl[] = [
  {
    id: 'control:magnet',
    label: 'Magnet',
    on: (c) => c.snapActive === true,
    name: (c) => (c.snapActive ? 'Magnet on' : 'Magnet'),
    icon: (on) => (on ? 'magnetOn' : 'magnetOff'),
    available: (c) => c.onSnapToggle !== undefined,
    run: (c) => c.onSnapToggle?.(),
  },
  {
    id: 'control:lockAll',
    label: 'Lock All',
    on: (c) => c.lockActive === true,
    name: (c) => (c.lockActive ? 'Unlock All' : 'Lock All'),
    icon: (on) => (on ? 'lock' : 'unlock'),
    available: (c) => c.onLockAll !== undefined,
    run: (c) => c.onLockAll?.(),
  },
  hideControl('control:hideDrawings', 'drawings'),
  hideControl('control:hideIndicators', 'indicators'),
  hideControl('control:hideAll', 'both'),
  {
    id: 'control:removeAll',
    label: 'Remove All',
    on: () => false,
    name: () => 'Remove All',
    icon: () => 'remove',
    available: (c) => c.onTrashAll !== undefined,
    run: (c) => c.onTrashAll?.(),
  },
]

const BY_ID = new Map(WHEEL_CONTROLS.map((c) => [c.id, c]))

/** The control a slot holds, or undefined for a drawing tool. */
export function wheelControl(name: string): WheelControl | undefined {
  return BY_ID.get(name)
}

/** A slot's name in menus, whichever kind it holds. */
export function wheelSlotLabel(name: string): string {
  return BY_ID.get(name)?.label ?? overlayLabel(name)
}
